import type React from 'react';
import { forwardRef } from 'react';

import { useStableCb } from 'bz-utilities/hooks/stable';
import type { FcProps } from 'bz-utilities/ts';

import { DropdownMenu } from '@zinc/ui';

import { useOnLocalChange, useValueMember } from './item-template';

export interface IProps<T> extends React.PropsWithChildren {
  item: T;
  'data-index': number;
  MenuProps?: FcProps<typeof DropdownMenu.Item>;
  style?: React.CSSProperties;
}

type GenericFRC = <T extends unknown>(
  ...args: Parameters<React.FC<IProps<T>>>
) => ReturnType<React.ForwardRefExoticComponent<IProps<T> & React.RefAttributes<unknown>>>;

const FieldSelectItem = forwardRef(<T extends { disabled?: boolean }>(props: IProps<T>, ref) => {
  const { item, style, MenuProps, ...other } = props;
  const onLocalChange = useOnLocalChange();
  const valueMember = useValueMember() as keyof T;

  const onClick = useStableCb(
    (e: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onLocalChange({ target: { value: item[valueMember] } });
    },
    [onLocalChange, item, valueMember]
  );

  return (
    <div style={style} ref={ref} {...other}>
      <DropdownMenu.Item
        key={item[valueMember] as React.Key}
        attributes={{
          style: { display: 'inline-block' }
        }}
        disabled={item.disabled}
        stopPropagation={true}
        onClick={onClick}
        {...MenuProps}
      >
        {props.children}
      </DropdownMenu.Item>
    </div>
  );
}) as GenericFRC;

export default FieldSelectItem;
